import { useState } from "react";
import { Formik } from "formik";
import { LocationCityRounded, ContactMailRounded } from "@mui/icons-material";
import { Divider, Typography, Box, Button, Grid } from "@mui/material";
import { useNavigate } from "react-router";
import { capitalize } from "lodash";
import AlertDialog from "../../common/AlertDialog";
import ApiKeyRevealDialog from "./ApiKeyRevealDialog";
import useAddClientForm from "../hooks/useAddClientForm";
import { addClientForm } from "./formSchema";
import ClientNameDetails from "../common/form-components/ClientNameDetails";
import ClientAddressDetails from "../common/form-components/ClientAddressDetails";
import ClientOwnerDetails from "../common/form-components/ClientOwnerDetails";
import ReadOnlyPricingPlan from "../common/form-components/ReadOnlyPricingPlan";
import ProductsSelect from "../common/form-components/ProductsSelect";
import {
  ImageUploader,
  ImageUploadProps,
} from "../common/form-components/ImageUploader";
import { ImageUploadValues } from "../types";
import IsPrivateLedgerCheckBox from "../common/form-components/IsPrivateLedgerCheckBox";
import routes from "../../../routes";

const formInitialValues = {
  "image-url": "",
  "client-name": "",
  "client-pricing-plan": "free",
  "client-products": [],
  "address-street": "",
  "address-zip-code": "",
  "address-city": "",
  "country-select": null,
  "main-user-first-name": "",
  "main-user-last-name": "",
  email: "",
  "is-private-ledger": false,
};

const style = {
  sectionIcon: { paddingRight: "10px", marginBottom: "5px" },
};

function AddClientForm() {
  const {
    handleSubmit,
    setConfirmationDialogOpen,
    isConfirmationDialogOpen,
    formSubmittalLoading,
    formSuccess,
    formAlert,
  } = useAddClientForm();
  const navigate = useNavigate();

  const [imageUploadValues] = useState<ImageUploadValues>({ file: null });

  const fileSizeInBytes = 2097152;

  const ImageUploadProp: ImageUploadProps = {
    width: "100%",
    height: "100%",
    editPage: false,
    maxFileSize: fileSizeInBytes,
  };

  const apiKey: string | undefined = formSuccess?.data?.client?.apiKey;
  const showApiKeyDialog =
    isConfirmationDialogOpen &&
    formAlert?.type === "success" &&
    Boolean(apiKey);

  const closeAndNavigate = () => {
    setConfirmationDialogOpen(false);
    navigate(routes.clients.view({ id: formSuccess?.data.client.id }));
  };

  return (
    <>
      <ApiKeyRevealDialog
        open={showApiKeyDialog}
        apiKey={apiKey ?? ""}
        onClose={closeAndNavigate}
      />
      <AlertDialog
        handleClose={() => {
          setConfirmationDialogOpen(false);
          if (formAlert?.type === "success") {
            navigate(routes.clients.view({ id: formSuccess?.data.client.id }));
          }
        }}
        open={isConfirmationDialogOpen && !showApiKeyDialog}
        title={capitalize(formAlert?.type) || ""}
        content={formAlert?.message || ""}
      />
      <Formik
        initialValues={formInitialValues}
        validationSchema={addClientForm}
        onSubmit={(form) => handleSubmit(form, imageUploadValues)}
        validateOnMount
        validateOnChange
        initialTouched={{
          "client-name": true,
          "client-pricing-plan": true,
          "address-zip-code": true,
          "address-city": true,
          "country-select": true,
          "main-user-first-name": true,
          "main-user-last-name": true,
          email: true,
          "is-private-ledger": false,
        }}
      >
        {({ handleSubmit, isValid }) => (
          <Grid
            data-testid="add-client-form"
            component="form"
            onSubmit={handleSubmit}
            container
            direction="column"
            spacing={8}
            sx={{ width: "50%" }}
          >
            <Grid data-testid="client-name-container">
              <Typography
                variant="h4"
                align="left"
                sx={{ marginBottom: "16px" }}
              >
                Add a new client
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  top: "0",
                  left: "0",
                  float: "left",
                  width: "25%",
                  aspectRatio: "1/1",
                  display: "flex",
                  margin: "1rem",
                  marginTop: "2rem",
                }}
              >
                <ImageUploader {...ImageUploadProp} />
              </Box>

              <Box sx={{ width: "65%", float: "right" }}>
                <ClientNameDetails />
                <ReadOnlyPricingPlan />
                <ProductsSelect />
              </Box>
              <Box
                sx={{
                  width: "65%",
                  float: "right",
                  display: "flex",
                  direction: "row",
                  alignItems: "center",
                }}
              >
                <IsPrivateLedgerCheckBox />
              </Box>
            </Grid>
            <Grid data-testid="client-address-container">
              <Divider textAlign="left" sx={{ marginBottom: "16px" }}>
                <Box sx={{ display: "flex", flexDiretion: "row" }}>
                  <LocationCityRounded
                    fontSize="large"
                    sx={style.sectionIcon}
                  />
                  <Typography variant="h5">Client Address</Typography>
                </Box>
              </Divider>
              <ClientAddressDetails />
            </Grid>
            <Grid data-testid="owner-details-container">
              <Divider textAlign="left" sx={{ marginBottom: "16px" }}>
                <Box display="flex" flexDirection="row">
                  <ContactMailRounded fontSize="large" sx={style.sectionIcon} />
                  <Typography variant="h5">Owner Details</Typography>
                </Box>
              </Divider>
              <ClientOwnerDetails
                alignmentStyles={{
                  flexWrap: "wrap",
                  justifyContent: "spaceBetween",
                  alignContent: "left",
                  direction: "row",
                }}
              />
            </Grid>
            <Grid display="flex" flexDirection="row" justifyContent="end">
              <Button
                onClick={() => navigate(-1)}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                loading={formSubmittalLoading}
                disabled={!isValid}
                type="submit"
                variant="contained"
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        )}
      </Formik>
    </>
  );
}

export default AddClientForm;
